import {useCallback} from 'react';

import type {Tag} from 'shells/tags-editor/tags-editor';

import type {ContactPageFragment, NutFileInput} from 'nutshell-graphql-types';

import {useHardDeleteContact} from '../graphql/hooks/use-hard-delete-contact';
import {useUpdateContact} from '../graphql/hooks/use-update-contact';

type Result = {
    handleUpdateName: (name: string) => Promise<any>;
    handleUpdateAvatar: (avatarFile: NutFileInput | null) => Promise<any>;
    handleUpdateAssignee: (assigneeId: string | null) => Promise<any>;
    handleUpdateDeletedStatus: (isDeleted: boolean) => Promise<any>;
    handleUpdateContactTerritory: (
        territoryId: string | null,
        territoryName: string | null
    ) => Promise<any>;
    handleUpdateDescription: (description: string) => Promise<any>;
    handleHardDelete: () => Promise<any>;
    handleUpdateTags: (tags: Tag[]) => Promise<any>;
};

export function useUpdateContactHandlers(contact?: ContactPageFragment | null): Result {
    const {updateContact} = useUpdateContact();
    const {hardDeleteContact} = useHardDeleteContact();

    const handleUpdateName = useCallback(
        (name: string) => {
            if (!contact) {
                return Promise.resolve();
            }

            return updateContact(
                {
                    id: contact.id,
                    name,
                },
                contact
            );
        },
        [updateContact, contact]
    );

    const handleUpdateAvatar = useCallback(
        (avatarFile: NutFileInput | null) => {
            if (!contact) {
                return Promise.resolve();
            }

            return updateContact({
                id: contact.id,
                avatarFile,
            });
        },
        [updateContact, contact]
    );

    const handleUpdateAssignee = useCallback(
        (assigneeId: string | null) => {
            if (!contact) {
                return Promise.resolve();
            }

            return updateContact(
                {
                    id: contact.id,
                    ownerId: assigneeId,
                },
                contact
            );
        },
        [updateContact, contact]
    );

    const handleUpdateDeletedStatus = useCallback(
        (isDeleted: boolean) => {
            if (!contact) {
                return Promise.resolve();
            }

            return updateContact(
                {
                    id: contact.id,
                    isDeleted,
                },
                contact
            );
        },
        [updateContact, contact]
    );

    const handleUpdateContactTerritory = useCallback(
        (territoryId: string | null, territoryName: string | null) => {
            if (!contact) {
                return Promise.resolve();
            }

            return updateContact(
                {
                    id: contact.id,
                    territoryId,
                },
                contact,
                territoryName
            );
        },
        [updateContact, contact]
    );

    const handleUpdateDescription = useCallback(
        (description: string) => {
            if (!contact) {
                return Promise.resolve();
            }

            return updateContact(
                {
                    id: contact.id,
                    description,
                },
                contact
            );
        },
        [updateContact, contact]
    );

    const handleHardDelete = useCallback(() => {
        if (!contact) {
            return Promise.resolve();
        }

        return hardDeleteContact(contact.id).then(() => {
            // force page reload and replace history entry to avoid returning to deleted page
            window.location.replace(`/people`);
        });
    }, [hardDeleteContact, contact]);

    const handleUpdateTags = useCallback(
        (tags: Tag[]) => {
            if (!contact) {
                return Promise.resolve();
            }

            return updateContact(
                {
                    id: contact.id,
                    tags: tags.map((tag) => ({tagType: 'CONTACT', name: tag.name})),
                },
                contact,
                undefined, // territoryName
                tags
            );
        },
        [updateContact, contact]
    );

    return {
        handleUpdateName,
        handleUpdateAvatar,
        handleUpdateAssignee,
        handleUpdateDeletedStatus,
        handleUpdateContactTerritory,
        handleUpdateDescription,
        handleHardDelete,
        handleUpdateTags,
    };
}
